import { Hand } from 'lucide-react'
import { useVoiceStore } from '../../stores/voiceStore'
import { getUserDisplayName } from '@os/messenger/web/components/telegram/TelegramChatList'

// OSPROD-263: floating "Raised hands" block over the right side of the
// call (ticket variant A). Purely presentational over the existing
// ephemeral raise-hand state (voiceStore.raisedHands, broadcast over the
// LiveKit data channel) — auto-appears when >= 1 hand is up, disappears
// when none. Visible to everyone in the call.
interface RaisedHandsParticipant {
  userId: string
  isSelf?: boolean
  user?: {
    username?: string
    firstName?: string
    lastName?: string | null
  } | null
}

export default function RaisedHandsPanel({
  participants,
}: {
  participants: RaisedHandsParticipant[]
}) {
  const raisedHands = useVoiceStore((s) => s.raisedHands)
  if (raisedHands.size === 0) return null

  // A JS Set preserves insertion order, so iterating raisedHands yields
  // hands in the order this client saw them raised. The LOCAL raise adds
  // both the DB user id and the LiveKit identity (toggleLocalRaisedHand's
  // dual-key robustness), so dedupe by the resolved participant. Keys
  // with no matching tile (participant already left) are skipped.
  const seen = new Set<string>()
  const rows: { key: string; name: string; isSelf: boolean }[] = []
  for (const key of raisedHands) {
    const p = participants.find((x) => x.userId === key)
    if (!p || seen.has(p.userId)) continue
    seen.add(p.userId)
    rows.push({
      key: p.userId,
      name: getUserDisplayName(p.user),
      isSelf: !!p.isSelf,
    })
  }
  if (rows.length === 0) return null

  return (
    <div className="absolute right-4 top-20 z-30 w-56 max-h-[50%] overflow-y-auto rounded-xl bg-card/95 backdrop-blur-sm ring-1 ring-border shadow-lg">
      <div className="sticky top-0 flex items-center gap-2 px-3 py-2 border-b border-border bg-card/95 backdrop-blur-sm rounded-t-xl">
        {/* Same yellow as the raise-hand control icon / tile badge (yellow-500). */}
        <Hand size={14} className="text-yellow-500" />
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Raised hands
        </span>
        <span className="ml-auto text-xs tabular-nums text-muted-foreground">{rows.length}</span>
      </div>
      <ul className="p-1.5">
        {rows.map((r, i) => (
          <li
            key={r.key}
            className="flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-foreground"
          >
            {/* Raise order — first raised speaks first. */}
            <span className="w-4 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">
              {i + 1}
            </span>
            <Hand size={13} className="shrink-0 text-yellow-500" />
            <span className="min-w-0 truncate">
              {r.name}
              {r.isSelf ? ' (you)' : ''}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
